import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, Users, UserCheck, TrendingUp, Clock, UserPlus } from 'lucide-react';
import { PerformanceData } from '@/hooks/usePerformanceData';

interface RecruiterMetricsCardsProps {
  data: PerformanceData | null;
  selectedRecruiterId: string | null;
}

export function RecruiterMetricsCards({ data, selectedRecruiterId }: RecruiterMetricsCardsProps) {
  if (!data) return null;

  const selectedRecruiter = selectedRecruiterId
    ? data.recruiters.find((r) => r.recruiterId === selectedRecruiterId)
    : null;

  const metrics = selectedRecruiter
    ? {
        openPositions: selectedRecruiter.openPositions,
        totalCandidates: selectedRecruiter.totalCandidates,
        candidatesInterviewed: selectedRecruiter.candidatesInterviewed,
        candidatesSelected: selectedRecruiter.candidatesSelected,
        interviewRate: selectedRecruiter.interviewRate,
        selectionRate: selectedRecruiter.selectionRate,
      }
    : {
        openPositions: data.recruiters.reduce((sum, r) => sum + r.openPositions, 0),
        totalCandidates: data.totalMetrics.totalCandidates,
        candidatesInterviewed: data.totalMetrics.totalInterviewed,
        candidatesSelected: data.totalMetrics.totalSelected,
        interviewRate:
          data.totalMetrics.totalCandidates > 0
            ? (data.totalMetrics.totalInterviewed / data.totalMetrics.totalCandidates) * 100
            : 0,
        selectionRate:
          data.totalMetrics.totalCandidates > 0
            ? (data.totalMetrics.totalSelected / data.totalMetrics.totalCandidates) * 100
            : 0,
      };

  const cards = [
    {
      title: 'Open Positions',
      value: metrics.openPositions,
      icon: Briefcase,
      description: 'Currently active jobs',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Total Candidates',
      value: metrics.totalCandidates,
      icon: Users,
      description: 'Candidates in pipeline',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    {
      title: 'Candidates Sourced',
      value: metrics.totalCandidates,
      icon: UserPlus,
      description: 'New candidates added',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Interviewed',
      value: metrics.candidatesInterviewed,
      icon: UserCheck,
      description: `${metrics.interviewRate.toFixed(1)}% interview rate`,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
    {
      title: 'Selected/Hired',
      value: metrics.candidatesSelected,
      icon: TrendingUp,
      description: `${metrics.selectionRate.toFixed(1)}% conversion rate`,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      title: 'Avg Time to Hire',
      value: selectedRecruiter?.avgTimeToHire ?? data.teamAverages.avgTimeToHire ?? '-',
      icon: Clock,
      description: 'Days to fill position',
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10',
      suffix: typeof (selectedRecruiter?.avgTimeToHire ?? data.teamAverages.avgTimeToHire) === 'number' ? ' days' : '',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
            <div className={`p-2 rounded-full ${card.bgColor}`}>
              <card.icon className={`h-4 w-4 ${card.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {card.value}
              {card.suffix}
            </div>
            <p className="text-xs text-muted-foreground">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
